import type { PlatformApiConfig } from './api-config';
import type { AuthenticationConfig } from './auth-config';
import type { ErrorLogConfig } from './error-config/error-log.config';
import type { TranslocoConfig } from '@jsverse/transloco';

/**
 * Logical environment type.
 *
 * The platform ships many build targets (local, preview, default, development, staging,
 * production, ...). Most are development variants that talk to the development backend and only
 * differ in tooling settings, so for behaviour and messaging we collapse them into three logical
 * types: development, staging and production.
 */
export const EnvironmentType = {
  Development: 'development',
  Staging: 'staging',
  Production: 'production',
  Demo: 'demo',
} as const;

export type EnvironmentType = (typeof EnvironmentType)[keyof typeof EnvironmentType];

export interface PlatformEnvironmentConfiguration {
  /**
   * Logical environment type
   */
  environmentType: EnvironmentType;

  /**
   * Generate serviceWorker
   */
  serviceWorker: boolean;

  /**
   * Base title to use for the application
   */
  baseTitle: string;

  /**
   * Backend/Api configuration
   */
  api?: PlatformApiConfig;

  /**
   * Key that will be used with google maps api's
   */
  googleKey: string;

  /**
   * projectId to be used with microsoft clarity
   */
  clarityProjectId?: string;

  /**
   * Cloudflare Turnstile site key, used by the bot protection on the login form.
   *
   * This is a public value — it ships in the browser bundle. The matching secret is held by the
   * backend and never reaches the frontend. Widgets are scoped to a hostname allowlist in
   * Cloudflare, so each environment should point at its own widget.
   */
  turnstileSiteKey: string;

  /**
   * Security settings
   */
  security: {
    /**
     * Enables a default TrustedTypesPolicy
     */
    defaultTrustedTypesPolicy: boolean;

    /**
     * Log removed DOM nodes to console, used for debugging
     */
    logRemovedDOM?: boolean;

    /**
     * something
     */
    routing?: {
      preventPathTraversalParams?: boolean;
      checkIdParams?: boolean;
      checkReferenceParams?: boolean;
    };
  };

  /**
   * Preview flag, this is for communication between a preview build and backend.
   * This will communicate that the connection is done from a preview hence we could keep the backend backwards compatible while providing new features only in the preview
   */
  previewFlag?: string;

  /**
   * Should hubspot be enabled
   */
  enableHubspot?: boolean;

  /**
   * Translation settings
   */
  transloco: {
    missingHandler?: Partial<TranslocoConfig['missingHandler']>;

    /**
     * Include test language for testing purposes?
     */
    includeTestLanguage: boolean;

    cache: {
      /**
       * ShortAgeThresholdMs is the lifetime in ms we use to evict items from storage when these entries do not match the currentLanguage
       */
      shortLifetime: number;

      /**
       * LongAgeThresholdMs is the life in ms we use to evict items from storage when these match the current language
       * This prevents indefinitely cached/stored entries ( we might be having translation scopes that are no longer part of the application )
       */
      longLifetime: number;
    };
  };

  /**
   * Specific feature settings
   */
  features: {
    showTrackingSimulation: boolean;
    /**
     * Shows the dev-only simulate control in the Inbox's Mailbox segment. UX gate only —
     * the backend endpoint 404s outside Local/Development regardless of this flag.
     */
    showEmailSimulation: boolean;
    /**
     * EmailService is deployed behind this environment's `/emailservice/`. Off, the Inbox has no Mailbox segment and
     * the order, trip, RFP and spot-quote Emails tables list no linked threads: no EmailService call is made at all.
     * Defaults FALSE (fail-safe: elsewhere its calls fall through to the SPA's nginx); on in development, preview,
     * local, monolith and the default `environment.ts`. Flip it per environment when EmailService is deployed there.
     */
    showEmailService: boolean;
    authentication: AuthenticationConfig;
    logger: ErrorLogConfig;
  };

  /**
   * Microsoft Authentication Library ( MSAL )
   */
  msalConfig: {
    auth: {
      clientID: string;
      authority: string;
    };
    apiConfig: {
      scopes: string[];
      uri: string;
    };
  };
}
